import { useEffect, useState } from "react";
import { CDSButton } from "@ciscodesignsystems/cds-react-button";
import { CDSCard } from "@ciscodesignsystems/cds-react-card";
import { CDSCheckbox } from "@ciscodesignsystems/cds-react-checkbox";
import { CDSContainer } from "@ciscodesignsystems/cds-react-container";
import { CDSFlex } from "@ciscodesignsystems/cds-react-flex";
import { CDSModal } from "@ciscodesignsystems/cds-react-modal";
import { CDSNotification } from "@ciscodesignsystems/cds-react-notification";
import { CDSTextArea } from "@ciscodesignsystems/cds-react-textarea";
import { CDSTextInput } from "@ciscodesignsystems/cds-react-text-input";
import { CDSTooltip } from "@ciscodesignsystems/cds-react-tooltip";
import { FILL_FORM_DATA_EVENT } from "../components/Footer";

const DRAFT_STORAGE_KEY = "uag.onboard.intake-draft";
const EXPANDED_SECTIONS_STORAGE_KEY = "uag.onboard.expanded-sections";
const DEMO_JIRA_URL = "https://cisco-sbg.atlassian.net/browse/CSEPCS-48724";
const DEMO_JIRA_KEY = "CSEPCS-48724";
const JIRA_LOAD_DELAY_MS = 2000;
const JIRA_CREATE_DELAY_MS = 3500;

const onboardingStages = [
  {
    title: "Intake questionnaire",
    detail: "Capture ownership, architecture, traffic, and release details before any onboarding work starts.",
  },
  {
    title: "Intake submitted",
    detail: "The completed questionnaire creates the Jira ticket that becomes the onboarding system of record.",
  },
  {
    title: "API team approval",
    detail: "SCG and API stakeholders review the intake, align on controls, and approve the implementation path.",
  },
  {
    title: "Onboard PR (QA -> Int)",
    detail: "Implementation moves through the lower environments first so routing, auth, and policy wiring are verified early.",
  },
  {
    title: "Automated test integration",
    detail: "Existing CI/CD validation is extended so gateway changes are exercised before promotion.",
  },
  {
    title: "Staging -> Production",
    detail: "After lower environments are stable, the same onboarding path advances toward the production rollout.",
  },
];

const environmentOptions = ["CI", "QA", "Int", "Preview", "Staging", "Production"];
const consumerOptions = ["Internal users", "External users", "Devices", "Backend services", "DevNet"];
const deploymentArchitectureOptions = ["Foundation EKS", "S3", "Other"];
const identityProviderOptions = ["PIAM", "CUI", "API Key", "Other"];
const capabilityOptions = [
  "Routing",
  "Authentication",
  "Authorization",
  "Rate limiting",
  "Logging",
  "Monitoring",
  "Request transformation",
  "Response transformation",
];
const loggingOptions = ["Datadog", "CloudWatch", "Splunk", "ELK", "Other"];
const optionGroups = {
  deploymentArchitecture: deploymentArchitectureOptions,
  environments: environmentOptions,
  consumerTypes: consumerOptions,
  identityProvider: identityProviderOptions,
  scgCapabilities: capabilityOptions,
  logLocations: loggingOptions,
};

const radioOptions = {
  networkExposure: [
    { key: "public", label: "Public internet", value: "Public internet" },
    { key: "allowlist", label: "Restricted allowlist", value: "Restricted allowlist" },
    { key: "private", label: "Private network only", value: "Private network only" },
    { key: "hybrid", label: "Hybrid", value: "Hybrid" },
  ],
  cachingRequirement: [
    { key: "none", label: "No caching", value: "No caching" },
    { key: "reads", label: "Cache reads", value: "Cache reads" },
    { key: "writes", label: "Cache writes", value: "Cache writes" },
    { key: "guidance", label: "Need guidance", value: "Need guidance" },
  ],
  latencyRequirement: [
    { key: "latency-under-100", label: "P95 < 100ms", value: "P95 < 100ms" },
    { key: "latency-under-250", label: "P95 < 250ms", value: "P95 < 250ms" },
    { key: "latency-under-500", label: "P95 < 500ms", value: "P95 < 500ms" },
    { key: "latency-under-1000", label: "P95 < 1s", value: "P95 < 1s" },
    { key: "latency-over-1000", label: "P95 > 1s", value: "P95 > 1s" },
  ],
  rateLimitRequirement: [
    { key: "no-rate", label: "No rate limits", value: "No rate limits" },
    { key: "defined-rate", label: "Defined rate limits", value: "Defined rate limits" },
    { key: "rate-guidance", label: "Need SCG recommendation", value: "Need SCG recommendation" },
  ],
  expensiveApis: [
    { key: "expensive-no", label: "No", value: "No" },
    { key: "expensive-yes", label: "Yes", value: "Yes" },
  ],
  errorTransformation: [
    { key: "transform-no", label: "No", value: "No" },
    { key: "transform-yes", label: "Yes", value: "Yes" },
  ],
  ciCdReadiness: [
    { key: "ready", label: "Ready now", value: "Ready now" },
    { key: "partial", label: "Partial automation", value: "Partial automation" },
    { key: "not-ready", label: "Not ready yet", value: "Not ready yet" },
  ],
  securityReviewStatus: [
    { key: "review-done", label: "Completed", value: "Completed" },
    { key: "review-progress", label: "In progress", value: "In progress" },
    { key: "review-none", label: "Not started", value: "Not started" },
  ],
};

const buildInitialFormState = () => ({
  serviceName: "",
  owningTeam: "",
  primaryContact: "",
  intakeSummary: "",
  deploymentArchitecture: [],
  deploymentArchitectureOther: "",
  networkExposure: "",
  environments: [],
  consumerTypes: [],
  trafficPatterns: "",
  scalabilityRequirements: "",
  latencyRequirement: "",
  cachingRequirement: "",
  cachingDetails: "",
  identityProvider: [],
  identityProviderOther: "",
  scgCapabilities: [],
  rateLimitRequirement: "",
  rateLimitDetails: "",
  expensiveApis: "",
  expensiveApisDetails: "",
  errorTransformation: "",
  errorTransformationDetails: "",
  logLocations: [],
  ciCdReadiness: "",
  ciCdDetails: "",
  oasLink: "",
  securityReviewStatus: "",
  targetTimeline: "",
  dependenciesAndRisks: "",
});

const buildDraftState = (formState, jiraLookupUrl, currentStageIndex) => ({
  formState,
  jiraLookupUrl,
  currentStageIndex,
});

const demoFormState = {
  serviceName: "Service Ownership API",
  owningTeam: "PIAM Service Ownership",
  primaryContact: "name@cisco.com",
  intakeSummary:
    "Service ownership APIs that will be onboarded behind SCG for internal product consumers and platform automation.",
  deploymentArchitecture: ["Foundation EKS", "Other"],
  deploymentArchitectureOther:
    "Regional Kubernetes workloads with managed data services and shared ingress in lower and production environments.",
  networkExposure: "Restricted allowlist",
  environments: ["QA", "Int", "Staging", "Production"],
  consumerTypes: ["Internal users", "Backend services", "DevNet"],
  trafficPatterns:
    "Steady internal traffic during business hours with moderate release-day spikes and predictable tenant sync bursts.",
  scalabilityRequirements:
    "Horizontal scaling on stateless services with autoscaling enabled for peak onboarding and synchronization windows.",
  latencyRequirement: "P95 < 500ms",
  cachingRequirement: "Need guidance",
  cachingDetails: "Read-heavy profile lookups may benefit from a short-lived cache with a 5 minute TTL.",
  identityProvider: ["PIAM", "Other"],
  identityProviderOther: "Okta",
  scgCapabilities: ["Authentication", "Authorization", "Rate limiting", "Logging", "Monitoring"],
  rateLimitRequirement: "Need SCG recommendation",
  rateLimitDetails: "Prefer tenant-aware rate limits with a modest burst allowance for bulk sync workflows.",
  expensiveApis: "Yes",
  expensiveApisDetails:
    "Bulk profile import and policy reconciliation endpoints can create larger payloads and longer processing windows.",
  errorTransformation: "No",
  errorTransformationDetails: "",
  logLocations: ["Datadog"],
  ciCdReadiness: "Ready now",
  ciCdDetails:
    "Smoke and regression suites already run in CI/CD and can be extended for SCG validation in lower environments.",
  oasLink: "https://github.com/cisco-sbg/example-repo/blob/main/openapi/service-ownership.yaml",
  securityReviewStatus: "In progress",
  targetTimeline: "QA and Int in April, staging in May, production in June",
  dependenciesAndRisks:
    "Pending final rate-limit guidance and production cutover alignment with the tenant provisioning team.",
};

const formSections = [
  {
    id: "ownership",
    title: "Service ownership",
    description: "Start with the service identity so the created Jira has the right owners.",
    fields: ["serviceName", "owningTeam", "primaryContact", "intakeSummary"],
  },
  {
    id: "architecture",
    title: "Architecture and deployment",
    description: "Capture the end-to-end flow, exposure model, and environments that need support.",
    fields: [
      "deploymentArchitecture",
      "deploymentArchitectureOther",
      "networkExposure",
      "environments",
      "consumerTypes",
    ],
  },
  {
    id: "traffic",
    title: "Traffic and reliability",
    description: "Use structured selections first, then add detail where needed.",
    fields: [
      "trafficPatterns",
      "scalabilityRequirements",
      "latencyRequirement",
      "cachingRequirement",
      "cachingDetails",
    ],
  },
  {
    id: "security",
    title: "Security and gateway requirements",
    description: "These are the most common follow-up items from SCG review.",
    fields: [
      "identityProvider",
      "identityProviderOther",
      "scgCapabilities",
      "rateLimitRequirement",
      "rateLimitDetails",
      "expensiveApis",
      "expensiveApisDetails",
      "errorTransformation",
      "errorTransformationDetails",
    ],
  },
  {
    id: "operations",
    title: "Operations and release readiness",
    description: "Finish with observability, CI/CD, artifacts, and timelines.",
    fields: [
      "logLocations",
      "ciCdReadiness",
      "ciCdDetails",
      "oasLink",
      "securityReviewStatus",
      "targetTimeline",
      "dependenciesAndRisks",
    ],
  },
];

const buildExpandedSectionsState = (isExpanded = false) => ({
  jiraImport: false,
  ...formSections.reduce(
    (sectionState, section) => ({
      ...sectionState,
      [section.id]: isExpanded,
    }),
    {}
  ),
});

const buildInitialExpandedSections = () => buildExpandedSectionsState(false);

const fieldDefinitions = {
  serviceName: {
    label: "Service or API name",
    type: "text",
    required: true,
    placeholder: "Identity Profile API",
  },
  owningTeam: {
    label: "Owning team",
    type: "text",
    required: true,
    placeholder: "PIAM Platform",
  },
  primaryContact: {
    label: "Primary contact",
    type: "text",
    required: true,
    placeholder: "name@cisco.com",
  },
  intakeSummary: {
    label: "What are you onboarding to SCG?",
    type: "textarea",
    required: true,
    placeholder: "Describe the service, key APIs, and the onboarding goal.",
    rows: 2,
  },
  deploymentArchitecture: {
    label: "Deployment architecture",
    type: "checkbox",
    required: true,
  },
  deploymentArchitectureOther: {
    label: "Other deployment architecture",
    type: "textarea",
    placeholder: "Describe the other deployment architecture details.",
    rows: 2,
  },
  networkExposure: {
    label: "How is the service exposed today?",
    type: "radio",
    required: true,
  },
  environments: {
    label: "Which SCG environments need onboarding?",
    type: "checkbox",
    required: true,
  },
  consumerTypes: {
    label: "Who consumes these APIs?",
    type: "checkbox",
    required: true,
  },
  trafficPatterns: {
    label: "Expected traffic volume and current trends",
    type: "textarea",
    required: true,
    placeholder: "Average load, trends, and peak windows.",
  },
  scalabilityRequirements: {
    label: "Scalability requirements",
    type: "textarea",
    required: true,
    placeholder: "Autoscaling, hard limits, resiliency expectations, and growth assumptions.",
  },
  latencyRequirement: {
    label: "Expected max API latency",
    type: "radio",
    required: true,
  },
  cachingRequirement: {
    label: "Caching requirement",
    type: "radio",
    required: true,
  },
  cachingDetails: {
    label: "Caching details",
    type: "textarea",
    placeholder: "TTL, endpoints, invalidation rules, or a note that caching is not needed yet.",
  },
  identityProvider: {
    label: "Identity provider or token issuer",
    type: "checkbox",
    required: true,
  },
  identityProviderOther: {
    label: "Other identity provider or token issuer",
    type: "text",
    placeholder: "Enter the other identity provider or token issuer.",
  },
  scgCapabilities: {
    label: "Which SCG capabilities are needed?",
    type: "checkbox",
    required: true,
  },
  rateLimitRequirement: {
    label: "Rate limit requirement",
    type: "radio",
    required: true,
  },
  rateLimitDetails: {
    label: "Rate limit examples",
    type: "textarea",
    placeholder: "Per API, per tenant, burst limits, or any thresholds you already know.",
  },
  expensiveApis: {
    label: "Any expensive or memory-heavy APIs?",
    type: "radio",
    required: true,
  },
  expensiveApisDetails: {
    label: "Describe the expensive APIs",
    type: "textarea",
    placeholder: "Large uploads, long-running jobs, streaming, or high-memory operations.",
  },
  errorTransformation: {
    label: "Should the gateway transform error codes or responses?",
    type: "radio",
    required: true,
  },
  errorTransformationDetails: {
    label: "Error transformation details",
    type: "textarea",
    placeholder: "Describe response normalization, masking, or mapping rules.",
  },
  logLocations: {
    label: "Where do application logs live?",
    type: "checkbox",
    required: true,
  },
  ciCdReadiness: {
    label: "Automated test pipeline status",
    type: "radio",
    required: true,
  },
  ciCdDetails: {
    label: "CI/CD and testing details",
    type: "textarea",
    required: true,
    placeholder: "Pipelines, smoke tests, release validation, and what SCG can invoke.",
  },
  oasLink: {
    label: "OpenAPI spec link",
    type: "text",
    required: true,
    placeholder: "https://github.com/.../openapi.yaml",
  },
  securityReviewStatus: {
    label: "Security review status",
    type: "radio",
    required: true,
  },
  targetTimeline: {
    label: "Target timeline",
    type: "text",
    required: true,
    placeholder: "Preview by May 15, production by June 30",
  },
  dependenciesAndRisks: {
    label: "Dependencies or known risks",
    type: "textarea",
    placeholder: "List blockers, cross-team dependencies, or open questions.",
  },
};

const requiredFields = [
  "serviceName",
  "owningTeam",
  "primaryContact",
  "intakeSummary",
  "deploymentArchitecture",
  "networkExposure",
  "environments",
  "consumerTypes",
  "trafficPatterns",
  "scalabilityRequirements",
  "latencyRequirement",
  "cachingRequirement",
  "identityProvider",
  "scgCapabilities",
  "rateLimitRequirement",
  "expensiveApis",
  "errorTransformation",
  "logLocations",
  "ciCdReadiness",
  "ciCdDetails",
  "oasLink",
  "securityReviewStatus",
  "targetTimeline",
];

const trimValue = (value) => String(value || "").trim();

const hasValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return trimValue(value).length > 0;
};

const getEventValue = (event) => event?.target?.value ?? event ?? "";

const getSafeExternalUrl = (value) => {
  const normalizedValue = trimValue(value);

  if (!normalizedValue) {
    return "";
  }

  try {
    const parsedUrl = new URL(normalizedValue);

    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return "";
    }

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

const getInitialBannerState = () => ({
  status: "",
  title: "",
  description: "",
  link: "",
});

const getFieldErrorMessage = (fieldKey) => {
  if (fieldKey === "serviceName") {
    return "Service or API name is required.";
  }

  if (fieldKey === "owningTeam") {
    return "Owning team is required.";
  }

  if (fieldKey === "primaryContact") {
    return "Primary contact is required.";
  }

  return "This field is required.";
};

const validateFormState = (formState) => {
  const nextErrors = {};

  requiredFields.forEach((fieldKey) => {
    if (!hasValue(formState[fieldKey])) {
      nextErrors[fieldKey] = getFieldErrorMessage(fieldKey);
    }
  });

  return nextErrors;
};

const focusField = (fieldKey) => {
  window.requestAnimationFrame(() => {
    const fieldElement =
      document.getElementById(fieldKey) ||
      document.querySelector(`[name="${fieldKey}"]`) ||
      document.querySelector(`[data-field-key="${fieldKey}"] input`) ||
      document.querySelector(`[data-field-key="${fieldKey}"] textarea`) ||
      document.querySelector(`[data-field-key="${fieldKey}"] button`);

    if (!fieldElement) {
      return;
    }

    fieldElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    fieldElement.focus();
  });
};

const getNextCheckboxSelections = (currentSelections, option, isChecked) => {
  if (isChecked) {
    return [...currentSelections, option];
  }

  return currentSelections.filter((currentOption) => currentOption !== option);
};

const isConditionalFieldVisible = (fieldKey, formState) => {
  if (fieldKey === "deploymentArchitectureOther") {
    return formState.deploymentArchitecture.includes("Other");
  }

  if (fieldKey === "identityProviderOther") {
    return formState.identityProvider.includes("Other");
  }

  if (fieldKey === "expensiveApisDetails") {
    return formState.expensiveApis === "Yes";
  }

  return true;
};

const renderCheckboxOptions = (fieldKey, options, formState, handleCheckboxChange) =>
  options.map((option) => (
    <CDSCheckbox
      key={option}
      checked={formState[fieldKey].includes(option)}
      onChange={handleCheckboxChange(fieldKey, option)}
    >
      {option}
    </CDSCheckbox>
  ));

const renderRadioOptions = (fieldKey, formState, handleTextChange) =>
  radioOptions[fieldKey].map((option) => (
    <label key={option.key} className="uag-onboard-radio-option">
      <input
        checked={formState[fieldKey] === option.value}
        name={fieldKey}
        onChange={handleTextChange(fieldKey)}
        type="radio"
        value={option.value}
      />
      <span>{option.label}</span>
    </label>
  ));

const OnBoardPage = () => {
  const [formState, setFormState] = useState(buildInitialFormState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [bannerState, setBannerState] = useState(getInitialBannerState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingJira, setIsLoadingJira] = useState(false);
  const [jiraLookupUrl, setJiraLookupUrl] = useState("");
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [isCreateJiraModalOpen, setIsCreateJiraModalOpen] = useState(false);
  const [didCopyJiraLink, setDidCopyJiraLink] = useState(false);
  const [expandedSections, setExpandedSections] = useState(buildInitialExpandedSections);

  useEffect(() => {
    try {
      const rawDraft = window.localStorage.getItem(DRAFT_STORAGE_KEY);

      if (!rawDraft) {
        return;
      }

      const parsedDraft = JSON.parse(rawDraft);
      const nextFormState = parsedDraft?.formState || parsedDraft;

      setFormState((currentFormState) => ({
        ...currentFormState,
        ...nextFormState,
      }));
      setExpandedSections(buildExpandedSectionsState(true));
      setJiraLookupUrl(parsedDraft?.jiraLookupUrl ?? "");
      setCurrentStageIndex(parsedDraft?.currentStageIndex ?? 0);
    } catch {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    try {
      const savedExpandedState = window.localStorage.getItem(EXPANDED_SECTIONS_STORAGE_KEY);

      if (savedExpandedState === null) {
        return;
      }

      const parsedExpandedState = JSON.parse(savedExpandedState);

      if (!parsedExpandedState || typeof parsedExpandedState !== "object") {
        return;
      }

      setExpandedSections((currentExpandedSections) => ({
        ...currentExpandedSections,
        ...parsedExpandedState,
      }));
    } catch {
      window.localStorage.removeItem(EXPANDED_SECTIONS_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const autoSaveTimer = window.setTimeout(() => {
      window.localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify(buildDraftState(formState, jiraLookupUrl, currentStageIndex))
      );
    }, 500);

    return () => {
      window.clearTimeout(autoSaveTimer);
    };
  }, [currentStageIndex, formState, jiraLookupUrl]);

  useEffect(() => {
    window.localStorage.setItem(
      EXPANDED_SECTIONS_STORAGE_KEY,
      JSON.stringify(expandedSections)
    );
  }, [expandedSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections((currentExpandedSections) => ({
      ...currentExpandedSections,
      [sectionId]: !currentExpandedSections[sectionId],
    }));
  };

  const clearFieldError = (fieldKey) => {
    if (!fieldErrors[fieldKey]) {
      return;
    }

    setFieldErrors((currentFieldErrors) => {
      const nextFieldErrors = { ...currentFieldErrors };
      delete nextFieldErrors[fieldKey];
      return nextFieldErrors;
    });
  };

  const handleTextChange = (fieldKey) => (event) => {
    const nextValue = getEventValue(event);

    setFormState((currentFormState) => ({
      ...currentFormState,
      [fieldKey]: nextValue,
    }));
    clearFieldError(fieldKey);
  };

  const handleCheckboxChange = (fieldKey, option) => (event) => {
    const isChecked = Boolean(event?.target?.checked);

    setFormState((currentFormState) => {
      const nextSelections = getNextCheckboxSelections(
        currentFormState[fieldKey],
        option,
        isChecked
      );

      return {
        ...currentFormState,
        [fieldKey]: nextSelections,
      };
    });
    clearFieldError(fieldKey);
  };

  const handleClearForm = () => {
    const shouldClearForm = window.confirm(
      "Are you sure you want to clear the intake form? This will remove the saved draft as well."
    );

    if (!shouldClearForm) {
      return;
    }

    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    setFormState(buildInitialFormState());
    setJiraLookupUrl("");
    setCurrentStageIndex(0);
    setExpandedSections(buildExpandedSectionsState(true));
    setFieldErrors({});
    setBannerState({
      status: "info",
      title: "Form cleared",
      description: "The intake form and saved draft were cleared.",
      link: "",
    });
    focusField("serviceName");
  };

  useEffect(() => {
    const handleFooterFillFormData = () => {
      setFormState(demoFormState);
      setExpandedSections(buildExpandedSectionsState(true));
      setFieldErrors({});
      setBannerState({
        status: "info",
        title: "Intake data loaded",
        description: `Onboarding data from ${DEMO_JIRA_KEY} was added to the form.`,
        link: DEMO_JIRA_URL,
      });
      focusField("serviceName");
    };

    window.addEventListener(FILL_FORM_DATA_EVENT, handleFooterFillFormData);

    return () => {
      window.removeEventListener(FILL_FORM_DATA_EVENT, handleFooterFillFormData);
    };
  }, []);

  const handleJiraLookupChange = (event) => {
    setJiraLookupUrl(getEventValue(event));
  };

  const handleCopyJiraLink = async () => {
    if (!navigator?.clipboard?.writeText) {
      return;
    }

    await navigator.clipboard.writeText(DEMO_JIRA_URL);
    setDidCopyJiraLink(true);
  };

  const syncFormFromJira = () => {
    setFormState(demoFormState);
    setExpandedSections(buildExpandedSectionsState(true));
    setFieldErrors({});
    setCurrentStageIndex(1);
  };

  const handleLoadJira = async () => {
    const nextJiraUrl = getSafeExternalUrl(jiraLookupUrl);

    if (!nextJiraUrl) {
      setBannerState({
        status: "negative",
        title: "Enter a valid Jira link",
        description: "Add a Jira issue URL before loading the service ownership data.",
        link: "",
      });
      return;
    }

    setIsLoadingJira(true);

    syncFormFromJira();
    try {
      await new Promise((resolve) => {
        window.setTimeout(resolve, JIRA_LOAD_DELAY_MS);
      });

      setBannerState({
        status: "success",
        title: `${DEMO_JIRA_KEY} loaded`,
        description:
          "The intake form was filled from the Jira service ownership record.",
        link: nextJiraUrl,
      });
      focusField("serviceName");
    } finally {
      setIsLoadingJira(false);
    }
  };

  const handleSubmit = async () => {
    const nextErrors = validateFormState(formState);
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setExpandedSections(buildExpandedSectionsState(true));
      setBannerState({
        status: "negative",
        title: "Complete the required fields",
        description: "Finish the highlighted items before creating the Jira intake ticket.",
        link: "",
      });
      focusField(requiredFields.find((fieldKey) => nextErrors[fieldKey]));

      return;
    }

    setIsSubmitting(true);
    setBannerState({
      status: "info",
      title: "Submitting intake",
      description: "Creating the Jira ticket and submitting the questionnaire payload.",
      link: "",
    });

    try {
      await new Promise((resolve) => {
        window.setTimeout(resolve, JIRA_CREATE_DELAY_MS);
      });

      setJiraLookupUrl(DEMO_JIRA_URL);
      setCurrentStageIndex(1);
      setDidCopyJiraLink(false);
      setBannerState({
        status: "success",
        title: `Jira created: ${DEMO_JIRA_KEY}`,
        description:
          "The intake questionnaire was submitted successfully and is ready for tracking in Jira.",
        link: DEMO_JIRA_URL,
      });
      setIsCreateJiraModalOpen(true);
    } catch {
      setBannerState(getInitialBannerState());
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTextField = (fieldKey) => {
    const field = fieldDefinitions[fieldKey];
    const errorMessage = fieldErrors[fieldKey];
    const commonProps = {
      id: fieldKey,
      label: field.label,
      value: formState[fieldKey],
      onChange: handleTextChange(fieldKey),
      placeholder: field.placeholder,
      status: errorMessage ? "negative" : undefined,
      helperText: errorMessage || undefined,
    };

    if (field.type === "textarea") {
      return (
        <div
          className={`uag-onboard-text-field ${field.rows && field.rows <= 2 ? "is-compact" : ""} ${errorMessage ? "is-invalid" : ""}`}
          data-field-key={fieldKey}
        >
          <CDSTextArea {...commonProps} rows={field.rows || 3} />
        </div>
      );
    }

    return (
      <div
        className={`uag-onboard-text-field ${errorMessage ? "is-invalid" : ""}`}
        data-field-key={fieldKey}
      >
        <CDSTextInput {...commonProps} />
      </div>
    );
  };

  const renderSelectableField = (fieldKey, fieldClassName, optionsContent) => {
    const field = fieldDefinitions[fieldKey];
    const errorMessage = fieldErrors[fieldKey];

    return (
      <div className={fieldClassName} data-field-key={fieldKey}>
        <div>
          <p className="uag-form-label">{field.label}</p>
          {errorMessage ? <p className="uag-onboard-field-error">{errorMessage}</p> : null}
        </div>
        <CDSFlex gap={16} wrap className="uag-onboard-option-row">
          {optionsContent}
        </CDSFlex>
      </div>
    );
  };

  const renderRadioField = (fieldKey) =>
    renderSelectableField(
      fieldKey,
      "uag-onboard-radio-field",
      renderRadioOptions(fieldKey, formState, handleTextChange)
    );

  const renderCheckboxGroup = (fieldKey) =>
    renderSelectableField(
      fieldKey,
      "uag-onboard-checkbox-group",
      renderCheckboxOptions(fieldKey, optionGroups[fieldKey], formState, handleCheckboxChange)
    );

  const renderField = (fieldKey) => {
    if (!isConditionalFieldVisible(fieldKey, formState)) {
      return null;
    }

    if (optionGroups[fieldKey]) {
      return renderCheckboxGroup(fieldKey);
    }

    if (fieldDefinitions[fieldKey].type === "radio") {
      return renderRadioField(fieldKey);
    }

    return renderTextField(fieldKey);
  };

  const renderSection = (section) => (
    <CDSCard key={section.id} className="uag-onboard-section-card">
      <CDSFlex direction="vertical" gap={16}>
        <button
          type="button"
          className="uag-onboard-section-toggle"
          aria-expanded={expandedSections[section.id]}
          onClick={() => toggleSection(section.id)}
        >
          <div className="uag-onboard-section-toggle-copy">
            <h2>{section.title}</h2>
            <p className="uag-section-copy">{section.description}</p>
          </div>
          <span
            aria-hidden="true"
            className={`uag-onboard-section-toggle-icon${expandedSections[section.id] ? " is-expanded" : ""}`}
          />
        </button>

        {expandedSections[section.id]
          ? section.fields.map((fieldKey) => <div key={fieldKey}>{renderField(fieldKey)}</div>)
          : null}
      </CDSFlex>
    </CDSCard>
  );

  return (
    <CDSContainer className="uag-onboard-page">
      {bannerState.status ? (
        <CDSNotification
          className="uag-onboard-notification"
          status={bannerState.status}
          title={bannerState.title}
          description={bannerState.description}
        >
          {bannerState.link ? (
            <a href={bannerState.link} target="_blank" rel="noreferrer">
              Open Jira
            </a>
          ) : null}
        </CDSNotification>
      ) : null}

      <CDSFlex direction="vertical" gap={16}>
        <div className="uag-page-heading">
          <h1>Onboard</h1>
          <p>
            Complete the SCG intake questionnaire first. After submission we create a Jira and use
            it to track the onboarding workflow through lower and production environments.
          </p>
        </div>

        <CDSCard className="uag-onboard-hero">
          <CDSFlex direction="vertical" gap={20} className="uag-onboard-hero-layout">
            <div>
              <div className="uag-onboard-flow-header">
                <div>
                  <h2>Onboarding flow</h2>
                  <p className="uag-section-copy">
                    The intake starts the Jira-driven onboarding path. The current step is driven
                    by the Jira onboarding state.
                  </p>
                </div>
              </div>
              <div className="uag-onboard-stage-row" aria-label="Onboarding flow">
                {onboardingStages.map((stage, index) => (
                  <CDSTooltip
                    key={stage.title}
                    disabled={index === currentStageIndex}
                    placement="top"
                    title={stage.detail}
                  >
                    <div
                      className={`uag-onboard-stage-chip ${index === currentStageIndex ? "is-active" : ""}`}
                    >
                      <span className="uag-onboard-stage-title">{stage.title}</span>
                    </div>
                  </CDSTooltip>
                ))}
              </div>
              <div className="uag-onboard-stage-detail" role="tabpanel">
                <span className="uag-onboard-stage-detail-label">Current step</span>
                <strong>{onboardingStages[currentStageIndex].title}</strong>
                <p>{onboardingStages[currentStageIndex].detail}</p>
              </div>
            </div>
          </CDSFlex>
        </CDSCard>

        <CDSCard className="uag-onboard-jira-card">
          <div
            className="uag-onboard-jira-collapse"
          >
            <button
              type="button"
              className="uag-onboard-section-toggle"
              aria-expanded={expandedSections.jiraImport}
              onClick={() => toggleSection("jiraImport")}
            >
              <div className="uag-onboard-section-toggle-copy">
                <h2>Import Service Ownership Jira</h2>
              </div>
              <span
                aria-hidden="true"
                className={`uag-onboard-section-toggle-icon${expandedSections.jiraImport ? " is-expanded" : ""}`}
              />
            </button>
            {expandedSections.jiraImport ? (
              <div className="uag-onboard-jira-panel" id="jira-import-panel">
                <p className="uag-section-copy">
                  Paste a Jira link to read the intake details from Jira and preload the onboarding
                  questionnaire.
                </p>
                <div className="uag-onboard-jira-controls">
                  <div className="uag-onboard-jira-input">
                    <CDSTextInput
                      id="jiraLookupUrl"
                      label="Jira link"
                      onChange={handleJiraLookupChange}
                      placeholder={DEMO_JIRA_URL}
                      value={jiraLookupUrl}
                    />
                  </div>
                  <CDSButton className="uag-onboard-jira-button" onClick={handleLoadJira}>
                    {isLoadingJira ? "Loading Jira..." : "Load data from Jira"}
                  </CDSButton>
                </div>
              </div>
            ) : null}
          </div>
        </CDSCard>

        <section className="uag-onboard-grid">
          <CDSFlex direction="vertical" gap={16} className="uag-onboard-form-column">
            {formSections.map(renderSection)}

            <CDSFlex gap={8} wrap>
              <CDSButton onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Creating Jira..." : "Create Jira and submit intake"}
              </CDSButton>
              <CDSButton variant="secondary" onClick={handleClearForm} disabled={isSubmitting}>
                Clear form
              </CDSButton>
            </CDSFlex>
          </CDSFlex>
        </section>
      </CDSFlex>

      {isSubmitting ? (
        <div className="uag-onboard-loading-overlay">
          <div className="uag-onboard-loading-panel">
            <div className="uag-onboard-spinner" aria-hidden="true" />
            <output
              className="uag-onboard-loading-status"
              aria-live="polite"
              htmlFor="onboard-submit-action"
            >
              Creating Jira
            </output>
            <p>Submitting the intake questionnaire and creating the Jira ticket.</p>
          </div>
        </div>
      ) : null}

      <CDSModal
        isOpen={isCreateJiraModalOpen}
        onClose={() => {
          setDidCopyJiraLink(false);
          setIsCreateJiraModalOpen(false);
        }}
      >
        <CDSFlex direction="vertical" gap={16} className="uag-onboard-modal">
          <div>
            <h2>Jira created</h2>
            <p className="uag-section-copy">
              Your intake has been submitted and the Jira ticket is ready.
            </p>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Jira link</span>
            <a href={DEMO_JIRA_URL} rel="noreferrer" target="_blank">
              {DEMO_JIRA_URL}
            </a>
          </div>
          <CDSFlex gap={8}>
            <CDSButton className="uag-onboard-jira-button" onClick={handleCopyJiraLink}>
              {didCopyJiraLink ? "Copied" : "Copy link"}
            </CDSButton>
            <CDSButton
              onClick={() => {
                setDidCopyJiraLink(false);
                setIsCreateJiraModalOpen(false);
              }}
            >
              Close
            </CDSButton>
          </CDSFlex>
        </CDSFlex>
      </CDSModal>
    </CDSContainer>
  );
};

export default OnBoardPage;

export const __testables = {
  buildDraftState,
  focusField,
  getEventValue,
  getNextCheckboxSelections,
  getSafeExternalUrl,
  validateFormState,
};
